import React, { useRef, useState, useEffect } from "react";
import { FormHelperText } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./TextEditor.scss";

// Helper function to get character count from content
export const getCharacterCount = (content) => {
  if (!content) return 0;
  const div = document.createElement("div");
  div.innerHTML = content;
  const txt = div.textContent || div.innerText || "";
  return txt.length;
};

export default function TextEditor({
  id = "",
  readOnly = false,
  value,
  onChange,
  onLimitExceeded = null,
  isError = false,
  errorMsg = "",
  placeholder,
  maxCharacters = 2000,
  showCharacterLimit = true,
  shouldSetId = false,
  toolbarOptions = [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
  ],
}) {
  const MAX_CHAR_LIMIT = maxCharacters;
  const quillRef = useRef(null);
  const scrollRef = useRef(0);

  const [editorValue, setEditorValue] = useState(value || "");
  const [charCount, setCharCount] = useState(0);
  const [pasteMessage, setPasteMessage] = useState("");
  const [showCharLimitError, setShowCharLimitError] = useState(false);

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const containsBase64Url = (content) => {
    const base64UrlPatterns = [
      /data:image\/[^;]+;base64,/gi,
      /data:application\/[^;]+;base64,/gi,
      /data:text\/[^;]+;base64,/gi,
      /data:audio\/[^;]+;base64,/gi,
      /data:video\/[^;]+;base64,/gi,
      /data:[^;]*;base64,/gi,
    ];
    return base64UrlPatterns.some((pattern) => pattern.test(content));
  };

  const handleChange = (content) => {
    scrollRef.current = window.scrollY; // save position before update
    const plainText = stripHtml(content);
    const length = plainText.length;

    if (containsBase64Url(content)) {
      showPasteMessage("Base64 URLs are not allowed.");
      return;
    }

    if (!showCharacterLimit || length <= MAX_CHAR_LIMIT) {
      setEditorValue(content);
      setCharCount(length);
      setShowCharLimitError(length >= MAX_CHAR_LIMIT);

      const elementId = quillRef.current?.getEditor?.().root?.id || "";
      onChange(content, quillRef.current, elementId);
    } else {
      setShowCharLimitError(true);
      if (onLimitExceeded) {
        onLimitExceeded(length, MAX_CHAR_LIMIT);
      }
    }

    // restore scroll after DOM update
    setTimeout(() => window.scrollTo(0, scrollRef.current), 0);
  };

  useEffect(() => {
    // prevent unnecessary re-renders
    if (value !== editorValue) {
      setEditorValue(value || "");
      const length = stripHtml(value || "").length;
      setCharCount(length);
      setShowCharLimitError(showCharacterLimit && length >= MAX_CHAR_LIMIT);
    }
  }, [value, showCharacterLimit, MAX_CHAR_LIMIT]);

  // Set the ID on the Quill editor root element only when shouldSetId is true
  useEffect(() => {
    if (shouldSetId && quillRef.current && id) {
      const editor = quillRef.current.getEditor();
      if (editor && editor.root) {
        editor.root.id = id;
      }
    }
  }, [shouldSetId, id]);

  // Handle paste safely
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();

      const handlePaste = (e) => {
        const clipboardData = e.clipboardData || window.clipboardData;
        const pastedData =
          clipboardData.getData("text/html") ||
          clipboardData.getData("text/plain");

        if (containsBase64Url(pastedData)) {
          e.preventDefault();
          showPasteMessage("Base64 URLs are not allowed.");
          return;
        }

        const plainText = stripHtml(pastedData);
        const currentText = stripHtml(editorValue);
        const selection = editor.getSelection();

        if (showCharacterLimit && selection) {
          const newLength =
            currentText.length - selection.length + plainText.length;

          if (newLength > MAX_CHAR_LIMIT) {
            e.preventDefault();
            const availableChars =
              MAX_CHAR_LIMIT - currentText.length + selection.length;

            if (availableChars > 0) {
              const trimmedText = plainText.substring(0, availableChars);
              editor.deleteText(selection.index, selection.length);
              editor.insertText(selection.index, trimmedText);
              const newContent = editor.root.innerHTML;
              setEditorValue(newContent);
              setCharCount(stripHtml(newContent).length);
              setShowCharLimitError(true);
              onChange(newContent, editor, editor.root.id || "");
            }

            if (onLimitExceeded) {
              onLimitExceeded(newLength, MAX_CHAR_LIMIT);
            }
          }
        }
      };

      editor.root.addEventListener("paste", handlePaste, true);
      return () => {
        editor.root.removeEventListener("paste", handlePaste, true);
      };
    }
  }, [editorValue, MAX_CHAR_LIMIT, showCharacterLimit, onLimitExceeded]);

  const handleBlur = () => {
    const editorInstance = quillRef.current?.getEditor?.();
    const content = editorInstance?.root?.innerHTML || "";
    const elementId = editorInstance?.root?.id || "";
    onChange(content, editorInstance, elementId);
  };

  const showPasteMessage = (msg) => {
    setPasteMessage(msg);
    setTimeout(() => setPasteMessage(""), 3000);
  };

  const formats = ["bold", "italic", "underline", "list", "bullet", "align"];

  return (
    <div className="overrideQuillEditor">
      {isError && stripHtml(editorValue).length === 0 && (
        <FormHelperText className="errorText">{errorMsg}</FormHelperText>
      )}

      <ReactQuill
        id={id}
        ref={quillRef}
        value={editorValue}
        onChange={handleChange}
        onBlur={handleBlur}
        modules={{ toolbar: toolbarOptions }}
        formats={formats}
        placeholder={placeholder || "Start typing..."}
        theme="snow"
        readOnly={readOnly}
      />

      {showCharacterLimit && (
        <div className="char-limit-msg">
          {charCount} / {MAX_CHAR_LIMIT} Characters
        </div>
      )}

      {showCharLimitError && showCharacterLimit && (
        <div
          className="error"
          style={{ color: "red", fontSize: "14px", marginTop: "4px" }}
        >
          Character limit exceeded, you can only enter {MAX_CHAR_LIMIT}{" "}
          characters
        </div>
      )}

      {pasteMessage && <div className="editor-error-msg">{pasteMessage}</div>}
    </div>
  );
}
